import type { ReactNode } from 'react';
import { cx } from '../cx';

/** An event on a time chart: at the index of an x position or period, numbered in order. */
export type ChartAnnotation = { index: number; label: ReactNode };

/**
 * The numbered markers on a time chart (LineChart, ColumnChart): a dotted line at the date with a
 * numbered ink marker at the top. `positionOf` maps an index to a left offset in percent. Events
 * only; never a claim that the event caused the change.
 */
export function AnnotationMarkers({
  annotations,
  positionOf,
}: {
  annotations: ChartAnnotation[];
  positionOf: (index: number) => number;
}) {
  return (
    <>
      {annotations.map((a, k) => (
        <span
          key={k}
          aria-hidden="true"
          className="absolute inset-y-0 z-10 border-l border-dotted border-jc-ink-secondary"
          style={{ left: `${positionOf(a.index)}%` }}
        >
          <span className="absolute -top-2.5 -left-2.5 grid size-5 place-items-center rounded-full bg-jc-ink font-display text-xs font-bold text-jc-page">
            {k + 1}
          </span>
        </span>
      ))}
    </>
  );
}

/** The notes under an annotated chart, numbered like the markers: what happened, when, and the source. */
export function AnnotationList({ annotations, className }: { annotations: ChartAnnotation[]; className?: string }) {
  return (
    <ol className={cx('flex list-decimal flex-col gap-1 pl-5 type-meta text-jc-ink-secondary', className)}>
      {annotations.map((a, k) => (
        <li key={k}>{a.label}</li>
      ))}
    </ol>
  );
}
